import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';

import {
    IFeature,
    IRole,
    CurrentUserDocument,
    ExtraUserFieldsDocument,
} from 'graphql/types';
import { useFeature } from 'common/hooks/useFeature';
import { useApolloError } from 'common/hooks/useApolloError';
import { IFilterPermissions, TFilterFormValues } from 'common/types';
import { useExtraFieldsFilters } from 'user/hooks/useExtraFieldsFilters';
import { getUserFilters } from 'user/constants/filter';
import { useUrlFilterParams } from 'common/hooks/useUrlFilterParams';
import {
    filtersToBase64String,
    filtersToQueryParams,
} from 'common/utils/filter';
import { IUserFilterValues } from 'user/types';
import { getUserExtraFieldsFilterValues } from 'user/utils/filter';

export const useUserFilter = (
    permissions?: IFilterPermissions,
    showEmptyFilterOptions = true,
    groupPriorityIds?: string[]
) => {
    const { showApolloError } = useApolloError();
    const [searchQueryParam, setSearchQueryParam] = useState('');
    const { filterParams, setFilterParam } = useUrlFilterParams('user');

    const { data: currentUserData, loading: currentUserLoading } =
        useQuery(CurrentUserDocument);
    const { currentUser } = currentUserData || {};
    const isGroupManager = currentUser?.isGroupManager;
    const isManager = currentUser?.roles?.includes(IRole.Manager);

    const canUseNonPublicExtraField =
        !currentUserLoading && (isManager || isGroupManager);

    const { canUse: hasUserActivity } = useFeature(IFeature.UserActivity);

    const { extraCategoryFilters } = useExtraFieldsFilters(
        canUseNonPublicExtraField
    );

    const filters = useMemo(
        () => [
            ...getUserFilters(
                permissions,
                showEmptyFilterOptions,
                hasUserActivity,
                groupPriorityIds
            ),
            ...extraCategoryFilters,
        ],
        [
            permissions,
            showEmptyFilterOptions,
            hasUserActivity,
            groupPriorityIds,
            extraCategoryFilters,
        ]
    );

    const {
        data: extraUserFieldsData,
        loading: extraUserFieldsLoading,
        error,
    } = useQuery(ExtraUserFieldsDocument);

    useEffect(() => {
        if (error) {
            showApolloError(error);
        }
    }, [error, showApolloError]);

    const { extraUserFields } = extraUserFieldsData || {};

    const selectedFilterParams = filterParams.filters;

    const handleSearch = (searchValue: string) => {
        setSearchQueryParam(searchValue);
    };

    const handleFilterChange = (values?: TFilterFormValues) => {
        const filterString = filtersToBase64String(filters, values);

        setFilterParam('filters', filterString);
    };

    const selectedFilters = useMemo(() => {
        if (!selectedFilterParams) return {};

        try {
            return JSON.parse(window.atob(selectedFilterParams));
        } catch {
            return {};
        }
    }, [selectedFilterParams]);

    const filterValuesForQuery = useMemo(() => {
        const filterValues = filtersToQueryParams<IUserFilterValues>(
            filters,
            selectedFilters
        );

        const { extraUserFields } = extraUserFieldsData || {};

        const extraFieldsObject = getUserExtraFieldsFilterValues(
            extraUserFields,
            filterValues
        );

        if (!!extraFieldsObject?.length) {
            filterValues.extraFields = extraFieldsObject;
        }

        const groupIds = filterValues?.groupIds as string[] | undefined;

        if (groupIds?.includes('noGroups')) {
            filterValues.groupIds = [];
        }

        const trainingIds = filterValues?.trainingIds as string[] | undefined;

        if (trainingIds?.includes('noTrainings')) {
            filterValues.trainingIds = [];
        }

        return filterValues;
    }, [filters, selectedFilters, extraUserFields]);

    return {
        filters,
        filterValues: { ...selectedFilters, q: searchQueryParam },
        filterValuesForQuery: {
            ...filterValuesForQuery,
            q: searchQueryParam,
        },
        loading: extraUserFieldsLoading,
        onSearch: handleSearch,
        onFilterChange: handleFilterChange,
    };
};
